import { ClockIcon, DownArrowIcon } from "@xola/icons";
import { Input, Popover, Tag, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { formatAMPM, parseTime } from "common";
import { getUserLocale } from "get-user-locale";
import React, { useState } from "react";
import { NoTranslate } from "../Common/NoTranslate";

export interface NewTimeslot {
    hours: number;
    minutes: number;
}

interface TimeslotTimePickerProps {
    isOpen?: boolean;
    value: number | null;
    onChange: (value: number, time: NewTimeslot | null) => void;
    isLoading?: boolean;
    error?: string;
    showTag?: boolean;
    className?: string;
    placeholder?: string;
    isMinimal?: boolean;
    onDelete?: () => void;
    onClickOutside?: (value: number | null, time: NewTimeslot | null) => void;
}

export const TimeslotTimePicker = ({
    isOpen = false,
    value,
    isLoading = false,
    error,
    onChange,
    showTag = false,
    className,
    placeholder = "Select Time",
    onDelete,
    onClickOutside,
    isMinimal = false,
}: TimeslotTimePickerProps) => {
    const time = value === null ? null : parseTime(value);
    const [showPicker, setShowPicker] = useState(isOpen);

    const handleMinuteClick = (minute: number) => {
        const value = (time?.hours ?? 0) * 100 + minute;
        onChange(value, parseTime(value));
        setShowPicker(false);
    };

    const handleHourClick = (hour: Hour) => {
        const formattedMinutes = time?.minutes ?? 0;
        const formattedTime = hour.metric * 100 + formattedMinutes;

        onChange(formattedTime, time);
    };

    return (
        <div className={className}>
            <Popover
                duration={[300, 250]}
                visible={showPicker}
                maxWidth="auto"
                appendTo={document.body}
                zIndex={40}
                onClickOutside={() => {
                    setShowPicker(false);
                    // Set Time on outside click
                    if (showTag) {
                        onClickOutside?.(value, time);
                    }
                }}
            >
                <ErrorTooltip error={error}>
                    {showTag ? (
                        <Tag
                            color="secondary"
                            size="medium"
                            className="mr-2 mb-2 cursor-pointer bg-gray-lighter"
                            onClick={() => setShowPicker(true)}
                            onClose={onDelete}
                        >
                            {time === null ? placeholder : <NoTranslate>{formatAMPM(time)}</NoTranslate>}
                        </Tag>
                    ) : (
                        <div className="relative flex w-full bg-gray-lighter">
                            {isMinimal ? null : (
                                <div className="pointer-events-none absolute inset-0 flex items-center pl-3">
                                    <ClockIcon className="z-10 inline-block" />
                                </div>
                            )}
                            <Input
                                readOnly
                                isError={!!error}
                                as="button"
                                className={clsx(
                                    "w-full cursor-pointer whitespace-nowrap text-left",
                                    isMinimal ? "" : "pl-8 pr-3",
                                    time === null ? "text-gray-dark" : null,
                                )}
                                disabled={isLoading}
                                onClick={() => setShowPicker(true)}
                            >
                                {time === null ? placeholder : <NoTranslate>{formatAMPM(time)}</NoTranslate>}
                            </Input>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                {!isMinimal && <DownArrowIcon className="inline-block" />}
                            </div>
                        </div>
                    )}
                </ErrorTooltip>

                <Popover.Content className="flex space-y-2 p-4">
                    <TimePicker time={time} onHourClick={handleHourClick} onMinuteClick={handleMinuteClick} />
                </Popover.Content>
            </Popover>
        </div>
    );
};

interface TimePickerProps {
    time: NewTimeslot | null;
    onHourClick: (value: Hour) => void;
    onMinuteClick: (value: number) => void;
}

export const TimePicker = ({ time, onHourClick, onMinuteClick }: TimePickerProps) => {
    const userLocale = getUserLocale();

    return (
        <>
            <div className="flex flex-col justify-evenly">
                <NoTranslate>AM</NoTranslate>
                <NoTranslate>PM</NoTranslate>
            </div>
            <div className="mr-2.5 flex flex-col border-r border-gray-light pr-2.5">
                <span className="text-center font-bold">Hour</span>
                <fieldset>
                    <div className="grid grid-cols-6 grid-rows-4 gap-y-2.5 gap-x-4 p-1.5">
                        {hours.map((hour) => (
                            <label
                                key={hour.metric}
                                htmlFor={String(`hour${hour.metric}`)}
                                className={clsx(
                                    "h-10 w-10 cursor-pointer rounded-full border border-gray-light p-2 text-center leading-relaxed hover:border-blue-light hover:bg-blue-lighter",
                                    time?.hours === hour.metric && "bg-blue-dark text-white",
                                )}
                            >
                                <input
                                    className="hidden"
                                    type="radio"
                                    name="hour"
                                    id={String(`hour${hour.metric}`)}
                                    value={formatTimeLabel(hour.metric)}
                                    onClick={() => onHourClick(hour)}
                                />
                                <NoTranslate>
                                    {isMetricHourFormat(userLocale) ? formatTimeLabel(hour.metric) : hour.imperial}
                                </NoTranslate>
                            </label>
                        ))}
                    </div>
                </fieldset>
            </div>

            <div className="flex flex-col">
                <span className="text-center font-bold">Minute</span>
                <fieldset>
                    <div className="grid grid-cols-3 grid-rows-4 gap-y-2.5 gap-x-4 p-1.5">
                        {minutes.map((minute) => {
                            return (
                                <label
                                    key={minute}
                                    htmlFor={String(`minute${minute}`)}
                                    className={clsx(
                                        "h-10 w-10 cursor-pointer rounded-full border border-gray-light p-2 text-center leading-relaxed hover:border-blue-light hover:bg-blue-lighter",
                                        time?.minutes === minute && "bg-blue-dark text-white",
                                    )}
                                >
                                    <input
                                        type="radio"
                                        className="hidden"
                                        id={String(`minute${minute}`)}
                                        name="minute"
                                        value={formatTimeLabel(minute)}
                                        onClick={() => onMinuteClick(minute)}
                                    />
                                    <NoTranslate>{formatTimeLabel(minute)}</NoTranslate>
                                </label>
                            );
                        })}
                    </div>
                </fieldset>
            </div>
        </>
    );
};

interface ErrorTooltipProps {
    error?: string;
    children: JSX.Element;
}

const ErrorTooltip = ({ error, children }: ErrorTooltipProps): JSX.Element => {
    return error ? <Tooltip content={error}>{children}</Tooltip> : children;
};

export interface Hour {
    metric: number;
    imperial: number;
}

const hours: Hour[] = [...Array.from({ length: 24 }).keys()].map((value) => {
    return { metric: value, imperial: value === 0 ? 12 : value < 13 ? value : value - 12 };
});
const minutes: number[] = [...Array.from({ length: 60 }).keys()].filter((key) => key % 5 === 0);

const formatTimeLabel = (number: number) => {
    return String(number).padStart(2, "0");
};

const isMetricHourFormat = (userLocale: string) => {
    const now = new Date().toLocaleString(userLocale);
    return !["AM", "PM"].some((string) => now.includes(string));
};
